import { useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '@campus/core';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Button } from '../ds/index.js';

/**
 * Valide qu'une URL est HTTPS (securite : pas de HTTP, pas de blob:, data:, javascript:).
 */
function isValidHttpsUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * IframeOverlay — Affiche une iframe quand l'avatar entre dans une zone "area" du TMJ.
 *
 * SECURITE :
 *   - URL validee cote client : HTTPS obligatoire
 *   - sandbox SANS allow-top-navigation (empeche redirect page parente)
 *   - postMessage filtre par origin strict
 *   - JWT jamais en localStorage — memoire Zustand + cookie HttpOnly
 */
export function IframeOverlay() {
  const activeArea = useGameStore((s) => s.activeArea);
  const dismissed = useGameStore((s) => s.iframeDismissed);
  const setDismissed = useGameStore((s) => s.setIframeDismissed);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isMobile = useIsMobile();

  // Origin autorisee — calculee depuis l'URL de la zone area
  const allowedOrigin = useCallback(() => {
    if (!activeArea) return null;
    try { return new URL(activeArea.url).origin; }
    catch { return null; }
  }, [activeArea?.url]);

  // postMessage filtre par origin strict
  useEffect(() => {
    const origin = allowedOrigin();
    if (!origin) return;

    const handler = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      switch (event.data?.type) {
        case 'iframe:close':
          setDismissed(true);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [allowedOrigin, setDismissed]);

  // Pas d'area active ou action launch → rien
  if (!activeArea || activeArea.action === 'launch') return null;

  // URL non HTTPS → bloquer
  if (!isValidHttpsUrl(activeArea.url)) return null;

  // Iframe fermee manuellement mais avatar toujours dans la zone → bouton "Rouvrir"
  if (dismissed) {
    return (
      <div className="pl-float-bottom">
        <Button variant="secondary" icon="grid" onClick={() => setDismissed(false)} style={{ boxShadow: 'var(--shadow-md)', pointerEvents: 'auto' }}>
          Rouvrir {activeArea.label}
        </Button>
      </div>
    );
  }

  // Iframe visible : panneau à droite (plein écran sur mobile)
  return (
    <aside
      aria-label={activeArea.label}
      style={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: isMobile ? '100%' : 'min(640px, 45%)',
        display: 'flex', flexDirection: 'column',
        background: 'var(--color-bg-surface)',
        borderLeft: isMobile ? 0 : '1px solid var(--color-border-default)',
        boxShadow: 'var(--shadow-lg)',
        pointerEvents: 'auto',
        zIndex: 'var(--z-panel)' as unknown as number,
        fontFamily: 'var(--font-body)', color: 'var(--color-text-primary)',
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: 'calc(8px + env(safe-area-inset-top, 0px)) 8px 8px 16px',
        borderBottom: '1px solid var(--color-border-default)',
      }}>
        <h2 style={{ margin: 0, font: '700 16px/1.25 var(--font-display)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeArea.label}
        </h2>
        <Button variant="ghost" icon="x" aria-label={`Fermer ${activeArea.label}`} onClick={() => setDismissed(true)} />
      </header>

      {/* iframe — sandbox SANS allow-top-navigation */}
      <iframe
        ref={iframeRef}
        src={activeArea.url}
        title={activeArea.label}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        referrerPolicy="no-referrer"
        style={{ flex: 1, width: '100%', border: 'none', background: '#fff' }}
        loading="lazy"
      />
    </aside>
  );
}
